/**
 * AciDefaultsForm Component
 *
 * Form for configuring Azure Container Instance default settings.
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T023 - Create AciDefaultsForm component
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Requirements:
 * - FR-003: Configure ACI resource group and subscription
 * - FR-004: Configure ACI resource defaults
 */

import React, { useState, useEffect } from 'react';
import {
  AciConfigDto,
  AcrConfigDto,
  KeyVaultConfigDto,
  InfrastructureConfigUpdateDto,
  RestartPolicy,
  RESTART_POLICIES,
} from '../types';
import { useUpdateInfrastructureConfig } from '../hooks/useInfrastructure';

interface AciDefaultsFormProps {
  initialData?: AciConfigDto;
  acrConfig?: AcrConfigDto;
  keyVaultConfig: KeyVaultConfigDto;
  onSuccess?: () => void;
}

interface FormErrors {
  resourceGroup?: string;
  subscriptionId?: string;
  cpuCores?: string;
  memoryGb?: string;
  timeoutMinutes?: string;
  location?: string;
  submit?: string;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RESOURCE_GROUP_REGEX = /^[a-zA-Z0-9_\-().]{1,90}$/;

const AZURE_LOCATIONS = [
  { value: 'eastus', label: 'East US' },
  { value: 'eastus2', label: 'East US 2' },
  { value: 'westus', label: 'West US' },
  { value: 'westus2', label: 'West US 2' },
  { value: 'centralus', label: 'Central US' },
  { value: 'northcentralus', label: 'North Central US' },
  { value: 'southcentralus', label: 'South Central US' },
  { value: 'westeurope', label: 'West Europe' },
  { value: 'northeurope', label: 'North Europe' },
  { value: 'uksouth', label: 'UK South' },
  { value: 'ukwest', label: 'UK West' },
];

export const AciDefaultsForm: React.FC<AciDefaultsFormProps> = ({
  initialData,
  acrConfig,
  keyVaultConfig,
  onSuccess,
}) => {
  const [formData, setFormData] = useState({
    resourceGroup: initialData?.resourceGroup || '',
    subscriptionId: initialData?.subscriptionId || '',
    cpuCores: initialData?.defaults?.cpuCores ?? 1.0,
    memoryGb: initialData?.defaults?.memoryGb ?? 2.0,
    timeoutMinutes: initialData?.defaults?.timeoutMinutes ?? 60,
    restartPolicy: initialData?.defaults?.restartPolicy ?? ('Never' as RestartPolicy),
    location: initialData?.defaults?.location || 'eastus2',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const updateMutation = useUpdateInfrastructureConfig();

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      setFormData({
        resourceGroup: initialData.resourceGroup || '',
        subscriptionId: initialData.subscriptionId || '',
        cpuCores: initialData.defaults?.cpuCores ?? 1.0,
        memoryGb: initialData.defaults?.memoryGb ?? 2.0,
        timeoutMinutes: initialData.defaults?.timeoutMinutes ?? 60,
        restartPolicy: initialData.defaults?.restartPolicy ?? 'Never',
        location: initialData.defaults?.location || 'eastus2',
      });
    }
  }, [initialData]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Resource group validation
    if (!formData.resourceGroup.trim()) {
      newErrors.resourceGroup = 'Resource group is required';
    } else if (!RESOURCE_GROUP_REGEX.test(formData.resourceGroup)) {
      newErrors.resourceGroup = 'Invalid resource group name format';
    }

    // Subscription ID validation
    if (!formData.subscriptionId.trim()) {
      newErrors.subscriptionId = 'Subscription ID is required';
    } else if (!UUID_REGEX.test(formData.subscriptionId)) {
      newErrors.subscriptionId = 'Subscription ID must be a valid UUID';
    }

    // CPU cores validation
    if (formData.cpuCores < 0.1 || formData.cpuCores > 4.0) {
      newErrors.cpuCores = 'CPU cores must be between 0.1 and 4.0';
    }

    // Memory validation
    if (formData.memoryGb < 0.5 || formData.memoryGb > 16.0) {
      newErrors.memoryGb = 'Memory must be between 0.5 and 16.0 GB';
    }

    // Timeout validation
    if (formData.timeoutMinutes < 1 || formData.timeoutMinutes > 1440) {
      newErrors.timeoutMinutes = 'Timeout must be between 1 and 1440 minutes';
    }

    // Memory to CPU ratio validation
    const ratio = formData.memoryGb / formData.cpuCores;
    if (ratio < 1 || ratio > 4) {
      newErrors.memoryGb = 'Memory to CPU ratio must be between 1:1 and 4:1';
    }

    // Location validation
    if (!formData.location) {
      newErrors.location = 'Location is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string | number): void => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const buildConfigPayload = (): InfrastructureConfigUpdateDto => ({
    acr: acrConfig || undefined,
    aci: {
      resourceGroup: formData.resourceGroup,
      subscriptionId: formData.subscriptionId,
      defaults: {
        cpuCores: formData.cpuCores,
        memoryGb: formData.memoryGb,
        timeoutMinutes: formData.timeoutMinutes,
        restartPolicy: formData.restartPolicy,
        location: formData.location,
      },
    },
    keyVault: keyVaultConfig,
  });

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();

    if (!validateForm()) return;

    setErrors({});

    try {
      await updateMutation.mutateAsync(buildConfigPayload());
      onSuccess?.();
    } catch (err: any) {
      const details: { field: string; message: string }[] = err?.response?.data?.details ?? [];
      if (details.length > 0) {
        setErrors({ submit: details.map(d => `${d.field}: ${d.message}`).join(' | ') });
      } else {
        setErrors({ submit: err instanceof Error ? err.message : 'Failed to save configuration' });
      }
    }
  };

  // Estimate cost
  const estimatedCostPerRun =
    (formData.cpuCores * 0.0448 + formData.memoryGb * 0.0049) * (formData.timeoutMinutes / 60);

  return (
    <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)]'>
      {/* Header */}
      <div className='px-6 py-4 border-b border-[var(--color-border)]'>
        <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>ACI Default Settings</h3>
        <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
          Configure default resource allocation for backup container instances
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className='p-6 space-y-6'>
        {/* Submit Error */}
        {errors.submit && (
          <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md text-sm'>
            {errors.submit}
          </div>
        )}

        {/* Azure Settings */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Resource Group */}
          <div>
            <label htmlFor='resourceGroup' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
              Resource Group <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              id='resourceGroup'
              value={formData.resourceGroup}
              onChange={(e) => handleInputChange('resourceGroup', e.target.value)}
              placeholder='rg-fabric-backup'
              className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.resourceGroup ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
              }`}
            />
            {errors.resourceGroup && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.resourceGroup}</p>
            )}
          </div>

          {/* Subscription ID */}
          <div>
            <label
              htmlFor='subscriptionId'
              className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'
            >
              Subscription ID <span className='text-red-500'>*</span>
            </label>
            <input
              type='text'
              id='subscriptionId'
              value={formData.subscriptionId}
              onChange={(e) => handleInputChange('subscriptionId', e.target.value)}
              placeholder='00000000-0000-0000-0000-000000000000'
              className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.subscriptionId ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
              }`}
            />
            {errors.subscriptionId && (
              <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.subscriptionId}</p>
            )}
          </div>
        </div>

        {/* Resource Allocation */}
        <div>
          <h4 className='text-sm font-medium text-[var(--color-text-primary)] mb-3'>Resource Allocation</h4>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            {/* CPU Cores */}
            <div>
              <label htmlFor='cpuCores' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
                CPU Cores
              </label>
              <input
                type='number'
                id='cpuCores'
                value={formData.cpuCores}
                onChange={(e) => handleInputChange('cpuCores', parseFloat(e.target.value))}
                min={0.1}
                max={4.0}
                step={0.1}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.cpuCores ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
                }`}
              />
              {errors.cpuCores ? (
                <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.cpuCores}</p>
              ) : (
                <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>0.1 - 4.0 vCPU</p>
              )}
            </div>

            {/* Memory */}
            <div>
              <label htmlFor='memoryGb' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
                Memory (GB)
              </label>
              <input
                type='number'
                id='memoryGb'
                value={formData.memoryGb}
                onChange={(e) => handleInputChange('memoryGb', parseFloat(e.target.value))}
                min={0.5}
                max={16.0}
                step={0.5}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.memoryGb ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
                }`}
              />
              {errors.memoryGb ? (
                <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.memoryGb}</p>
              ) : (
                <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>0.5 - 16.0 GB</p>
              )}
            </div>

            {/* Timeout */}
            <div>
              <label
                htmlFor='timeoutMinutes'
                className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'
              >
                Timeout (minutes)
              </label>
              <input
                type='number'
                id='timeoutMinutes'
                value={formData.timeoutMinutes}
                onChange={(e) => handleInputChange('timeoutMinutes', parseInt(e.target.value))}
                min={1}
                max={1440}
                className={`w-full px-3 py-2 border rounded-md shadow-sm bg-[var(--color-bg-card)] text-[var(--color-text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.timeoutMinutes ? 'border-red-300 dark:border-red-600' : 'border-[var(--color-border)]'
                }`}
              />
              {errors.timeoutMinutes ? (
                <p className='mt-1 text-sm text-red-600 dark:text-red-400'>{errors.timeoutMinutes}</p>
              ) : (
                <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>1 - 1440 min</p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Settings */}
        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          {/* Restart Policy */}
          <div>
            <label htmlFor='restartPolicy' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
              Restart Policy
            </label>
            <select
              id='restartPolicy'
              value={formData.restartPolicy}
              onChange={(e) => handleInputChange('restartPolicy', e.target.value as RestartPolicy)}
              className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              {RESTART_POLICIES.map((policy) => (
                <option key={policy} value={policy}>
                  {policy}
                </option>
              ))}
            </select>
            <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
              Never = exit on completion, OnFailure = retry on error
            </p>
          </div>

          {/* Location */}
          <div>
            <label htmlFor='location' className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>
              Location
            </label>
            <select
              id='location'
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
            >
              {AZURE_LOCATIONS.map((loc) => (
                <option key={loc.value} value={loc.value}>
                  {loc.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Cost Estimate */}
        <div className='bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md p-4'>
          <h4 className='text-sm font-medium text-blue-800 dark:text-blue-300 mb-2'>Estimated Cost</h4>
          <div className='text-sm text-blue-700 dark:text-blue-400'>
            <p>
              Per backup run: <span className='font-medium'>${estimatedCostPerRun.toFixed(4)}</span>
            </p>
            <p>
              Monthly (30 runs):{' '}
              <span className='font-medium'>${(estimatedCostPerRun * 30).toFixed(2)}</span>
            </p>
          </div>
          <p className='text-xs text-blue-600 dark:text-blue-400 mt-2'>
            Estimates based on average Azure ACI pricing. Actual costs may vary by region.
          </p>
        </div>

        {/* Actions */}
        <div className='flex items-center justify-end pt-4'>
          <button
            type='submit'
            disabled={updateMutation.isPending}
            className='px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {updateMutation.isPending ? (
              <span className='flex items-center'>
                <svg className='animate-spin -ml-1 mr-2 h-4 w-4' fill='none' viewBox='0 0 24 24'>
                  <circle
                    className='opacity-25'
                    cx='12'
                    cy='12'
                    r='10'
                    stroke='currentColor'
                    strokeWidth='4'
                  />
                  <path
                    className='opacity-75'
                    fill='currentColor'
                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                  />
                </svg>
                Saving...
              </span>
            ) : (
              'Save Defaults'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AciDefaultsForm;
